import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import InternalLayout from '../../components/InternalLayout';
import ReasonModal from '../../components/ReasonModal';
import { internalApi } from '../../utils/api';

// PRD-008 F8 / AC-4 — workspace drill-down with reason gate. The
// reason modal renders before any drill-down content, and the audit
// row is written server-side on the same request that returns the data.
const TABS = [
  { key: 'members', label: 'Members' },
  { key: 'agents', label: 'Agents' },
  { key: 'connections', label: 'Connections' },
  { key: 'proposals', label: 'Proposals' },
  { key: 'agreements', label: 'Agreements' },
  { key: 'bookings', label: 'Bookings' },
  { key: 'consumption', label: 'Consumption' },
];

function Cell({ children }) {
  return <td className="px-3 py-2 text-sm text-gray-700 align-top">{children || '—'}</td>;
}

export default function OperatorWorkspaceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [reasonOpen, setReasonOpen] = useState(true);
  const [tab, setTab] = useState('members');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [impersonating, setImpersonating] = useState(false);
  const [impersonateModalOpen, setImpersonateModalOpen] = useState(false);

  const fetchDetail = async (reason) => {
    setLoading(true);
    setError(null);
    try {
      const res = await internalApi.getWorkspace(id, reason);
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.error_code || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleImpersonate = async (reason) => {
    setImpersonating(true);
    setError(null);
    try {
      const res = await internalApi.startImpersonation(id, reason);
      // Stash the impersonation token + context in sessionStorage so the
      // axios interceptor picks it up for customer-facing requests, and
      // the persistent banner has the data it needs.
      sessionStorage.setItem('operatorImpersonationToken', res.data.impersonation_token);
      sessionStorage.setItem(
        'operatorImpersonationContext',
        JSON.stringify({
          sessionId: res.data.audit_log_id,
          workspaceId: res.data.effective_workspace_id,
          workspaceName: data?.workspace?.name || '',
          effectiveUserId: res.data.effective_user_id,
          startedAt: new Date().toISOString(),
        })
      );
      navigate('/dashboard');
      // Force a reload so the auth context picks up the impersonation
      // token through /api/auth/me.
      window.location.reload();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setImpersonating(false);
      setImpersonateModalOpen(false);
    }
  };

  return (
    <InternalLayout pageTitle={data?.workspace?.name || 'Workspace'}>
      <ReasonModal
        open={reasonOpen}
        title="Why are you viewing this workspace?"
        description="Your reason will be recorded in the platform audit log and may be reviewed."
        confirmLabel="View workspace"
        onConfirm={async (reason) => { setReasonOpen(false); await fetchDetail(reason); }}
        onCancel={() => navigate('/internal/workspaces')}
      />
      <ReasonModal
        open={impersonateModalOpen}
        title="Start an operator impersonation session"
        description="You will act as a member of this workspace. Every action you take will be audit-logged with this reason."
        confirmLabel="Start session"
        onConfirm={handleImpersonate}
        onCancel={() => setImpersonateModalOpen(false)}
      />

      {error && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-800">{error}</div>
      )}

      {loading && <div className="text-gray-600 text-sm">Loading workspace detail…</div>}

      {data && (
        <>
          <div className="bg-white rounded-md border border-gray-200 shadow-sm p-4 mb-4 flex items-start justify-between">
            <div>
              <div className="text-xs text-gray-600 uppercase tracking-wide">Workspace identity</div>
              <div className="text-lg font-semibold text-gray-900 mt-1">{data.workspace.name}</div>
              <div className="text-sm text-gray-600">{data.workspace.slug}</div>
              <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-sm text-gray-700 max-w-md">
                <span>Tier: <span className="text-gray-900">{data.workspace.tier || '—'}</span></span>
                <span>Domain: <span className="text-gray-900">{data.workspace.domain || '—'}</span></span>
                <span>Members: <span className="text-gray-900">{data.members.length}</span></span>
                <span>Agents: <span className="text-gray-900">{data.agents.length}</span></span>
                <span>Connections: <span className="text-gray-900">{data.connections.length}</span></span>
                <span>Agreements: <span className="text-gray-900">{data.agreements.length}</span></span>
              </div>
            </div>
            <button
              onClick={() => setImpersonateModalOpen(true)}
              disabled={impersonating}
              className="px-3 py-1.5 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-md disabled:opacity-50"
            >
              {impersonating ? 'Starting…' : 'View as member'}
            </button>
          </div>

          <div className="bg-white rounded-md border border-gray-200 shadow-sm">
            <div className="border-b border-gray-200 flex overflow-x-auto">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 ${
                    tab === t.key
                      ? 'border-slate-700 text-slate-900'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <div className="p-4">
              {tab === 'members' && <SimpleTable
                rows={data.members}
                columns={[
                  ['email', 'Email'],
                  ['name', 'Name'],
                  ['role', 'Role'],
                  ['joined_at', 'Joined'],
                  ['is_platform_operator', 'Operator'],
                ]}
              />}
              {tab === 'agents' && <SimpleTable
                rows={data.agents}
                columns={[['name', 'Name'], ['description', 'Description'], ['agent_created_at', 'Created']]}
              />}
              {tab === 'connections' && <SimpleTable
                rows={data.connections}
                columns={[
                  ['id', 'ID'],
                  ['state', 'State'],
                  ['seller_workspace_name', 'Seller'],
                  ['buyer_workspace_name', 'Buyer'],
                  ['updated_at', 'Updated'],
                ]}
              />}
              {tab === 'proposals' && <SimpleTable
                rows={data.proposals}
                columns={[['name', 'Name'], ['status', 'Status'], ['updated_at', 'Updated']]}
              />}
              {tab === 'agreements' && <SimpleTable
                rows={data.agreements}
                columns={[['name', 'Name'], ['status', 'Status'], ['signed_at', 'Signed']]}
              />}
              {tab === 'bookings' && <SimpleTable
                rows={data.bookings}
                columns={[['description', 'Description'], ['amount', 'Amount'], ['currency', 'Currency'], ['status', 'Status'], ['due_date', 'Due']]}
              />}
              {tab === 'consumption' && <SimpleTable
                rows={data.recent_consumption_entries}
                columns={[['quantity', 'Quantity'], ['unit_cost_at_time', 'Unit cost'], ['total_cost', 'Total'], ['timestamp', 'Timestamp']]}
              />}
            </div>
          </div>

          <div className="mt-4">
            <Link to="/internal/workspaces" className="text-sm text-slate-700 hover:underline">← Back to workspace list</Link>
          </div>
        </>
      )}
    </InternalLayout>
  );
}

function SimpleTable({ rows, columns }) {
  if (!rows || rows.length === 0) {
    return <div className="text-sm text-gray-600 italic">None.</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            {columns.map(([key, label]) => (
              <th key={key} className="text-left px-3 py-2 font-medium text-gray-600">{label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || i} className="border-t border-gray-100">
              {columns.map(([key]) => <Cell key={key}>{formatValue(r[key])}</Cell>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function formatValue(v) {
  if (v == null) return null;
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  if (v instanceof Date) return v.toISOString();
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) {
    return new Date(v).toLocaleString();
  }
  return String(v);
}
